import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { AuthShell } from '../components/AuthShell';
import { checkResetToken, resetPassword } from '../api/auth';
import { ApiError } from '../api/client';

/**
 * Password reset - token-consumption stage.
 *
 * URL: /reset-password/:token
 *
 * Three states the user can land in:
 *   1. Token valid → render the new-password form.
 *   2. Token expired or invalid → render a "request a new link"
 *      message with a CTA back to /reset-password.
 *   3. Password reset succeeded → render a success state with a
 *      CTA to /login. We do NOT auto-login (matches the form
 *      handler) so the user has to type the new password to
 *      verify they remembered it correctly.
 *
 * The check uses GET /api/auth/reset-password/check (side-effect-
 * free) on mount; the SPA only POSTs the actual /reset-password
 * when the user submits the form.
 */
export function ResetToken() {
  const { token = '' } = useParams<{ token: string }>();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const checkQuery = useQuery({
    queryKey: ['reset-token-check', token],
    queryFn: () => checkResetToken(token),
    staleTime: 60_000,
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () => resetPassword(token, password),
    onSuccess: () => setDone(true),
    onError: (err) => {
      setErrorMessage(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long.');
      return;
    }
    mutation.mutate();
  }

  // ── Loading the token check ─────────────────────────────────────
  if (checkQuery.isLoading) {
    return (
      <AuthShell>
        <p style={{ textAlign: 'center', color: 'var(--text-dim)' }}>
          Verifying reset link…
        </p>
      </AuthShell>
    );
  }

  // ── Done (success) ──────────────────────────────────────────────
  if (done) {
    return (
      <AuthShell
        title="Password updated"
        subtitle="Your password has been reset. Sign in with your new password to continue."
        footer={null}
      >
        <Link to="/login" className="auth-btn" style={{ textDecoration: 'none' }}>
          Go to sign in
        </Link>
      </AuthShell>
    );
  }

  // ── Token check returned not-valid OR endpoint errored ──────────
  const checkData = checkQuery.data;
  const isInvalidToken =
    checkQuery.isError ||
    (checkData && checkData.valid === false);
  if (isInvalidToken) {
    const reason = checkData?.reason;
    const message =
      reason === 'expired'
        ? 'This reset link has expired. Reset links are valid for 1 hour.'
        : 'This reset link is invalid. It may have been used already, or the URL was mistyped.';
    return (
      <AuthShell
        title="Link no longer valid"
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <div className="auth-alert auth-alert-warning" role="alert">
          {message}
        </div>
        <Link
          to="/reset-password"
          className="auth-btn auth-btn-secondary"
          style={{ textDecoration: 'none' }}
        >
          Request a new reset link
        </Link>
      </AuthShell>
    );
  }

  // ── Default: render the new-password form ──────────────────────
  return (
    <AuthShell
      title="Set a new password"
      subtitle="Choose a password of at least 8 characters."
      footer={<Link to="/login">Back to sign in</Link>}
    >
      {errorMessage && (
        <div className="auth-alert auth-alert-danger" role="alert">
          {errorMessage}
        </div>
      )}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <input
          className="auth-input"
          type="password"
          name="password"
          autoComplete="new-password"
          placeholder="New password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={mutation.isPending}
          minLength={8}
          required
        />
        <input
          className="auth-input"
          type="password"
          name="confirm_password"
          autoComplete="new-password"
          placeholder="Confirm new password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={mutation.isPending}
          minLength={8}
          required
        />
        <button
          type="submit"
          className="auth-btn"
          disabled={mutation.isPending || !password || !confirmPassword}
        >
          {mutation.isPending ? 'Updating…' : 'Reset password'}
        </button>
      </form>
    </AuthShell>
  );
}
